import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import FormInput from '../../components/forms/formInput';
import { getSelf, updateProfile } from '../../services/users';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { roleLabel } from '../../constants/labels';
import AvatarUpload from '../../components/avatarUpload';
import { uploadUserAvatar } from '../../services/companyUsers';
import { SUPPORTED_LANGUAGES } from '../../i18n/i18n';

const pageStyles = `
  .sbr .setwrap{display:grid;grid-template-columns:228px 1fr;gap:20px;align-items:start}
  @media(max-width:960px){.sbr .setwrap{grid-template-columns:1fr}}
  .sbr .setnav{background:#fff;border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-2);padding:8px;position:sticky;top:16px}
  .sbr .setnav .cap{font-size:10.5px;font-weight:800;letter-spacing:.7px;text-transform:uppercase;color:#9AA4B2;padding:10px 12px 7px}
  .sbr .snav{display:flex;align-items:center;gap:11px;padding:10px 12px;border-radius:11px;color:var(--text);
    font-size:13.5px;font-weight:600;cursor:pointer;margin-bottom:2px;text-decoration:none;transition:.14s;border:0;background:none;width:100%}
  .sbr .snav .ic{width:17px;height:17px;color:#94A3B8}
  .sbr .snav:hover{background:var(--surface);color:var(--ink)}
  .sbr .snav.active{background:var(--brand-50);color:var(--brand-ink)}
  .sbr .snav.active .ic{color:var(--brand)}
  .sbr .snav .chev{margin-left:auto;width:15px;height:15px;color:#C4CCD8}
  .sbr .panel{background:#fff;border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-2);overflow:hidden}
  .sbr .panel .phd{display:flex;align-items:center;gap:14px;padding:20px 24px;border-bottom:1px solid var(--line);background:linear-gradient(180deg,#FBFCFF,#fff)}
  .sbr .panel .phd .ti{font-size:17px;font-weight:800;color:var(--ink);letter-spacing:-.3px}
  .sbr .panel .phd .ds{font-size:12.5px;color:var(--muted);font-weight:500;margin-top:2px}
  .sbr .panel .phd .badge-soft{margin-left:auto;font-size:11px;font-weight:800;letter-spacing:.4px;text-transform:uppercase;
    color:var(--good);background:var(--good-bg);border:1px solid var(--good-bd);border-radius:999px;padding:5px 11px;white-space:nowrap}
  .sbr .pbody{padding:24px}
  .sbr .avrow{display:flex;align-items:center;gap:18px;padding-bottom:22px;margin-bottom:22px;border-bottom:1px solid var(--line-soft)}
  .sbr .avbig{width:72px;height:72px;border-radius:22px;background:linear-gradient(160deg,#34D399,#0EA5E9);color:#fff;
    font-size:24px;font-weight:800;display:flex;align-items:center;justify-content:center;box-shadow:var(--sh-2);flex-shrink:0;letter-spacing:-.5px}
  .sbr .avmeta .nm{font-size:17px;font-weight:800;color:var(--ink);letter-spacing:-.3px}
  .sbr .avmeta .rl{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:700;color:var(--brand-ink);
    background:var(--brand-50);border:1px solid var(--brand-100);border-radius:999px;padding:4px 11px;margin-top:7px}
  .sbr .avmeta .rl .ic{width:13px;height:13px}
  .sbr .avact{margin-left:auto;display:flex;gap:8px;flex-wrap:wrap}
  .sbr .fgrid{display:grid;grid-template-columns:1fr 1fr;gap:18px 22px}
  @media(max-width:700px){.sbr .fgrid{grid-template-columns:1fr}}
  .sbr .field{display:flex;flex-direction:column;gap:7px}
  .sbr .field.full{grid-column:1 / -1}
  .sbr .field label{font-size:12.5px;font-weight:700;color:var(--text);display:flex;align-items:center;gap:6px}
  .sbr .field label .ic{width:13px;height:13px;color:var(--faint)}
  .sbr .field .hint{font-size:11.5px;color:var(--muted);font-weight:500;display:flex;align-items:center;gap:5px;margin-top:2px}
  .sbr .field .hint .ic{width:12px;height:12px;color:var(--faint)}
  .sbr .inp{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid var(--line);border-radius:var(--r-sm);
    padding:11px 13px;font-size:14px;font-weight:600;color:var(--ink);box-shadow:var(--sh-1);transition:.14s}
  .sbr .inp:focus-within{border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-50)}
  .sbr .inp input{border:0;outline:0;background:transparent;flex:1;font-family:inherit;font-size:14px;font-weight:600;color:var(--ink);min-width:0}
  .sbr .inp input::placeholder{color:var(--faint);font-weight:500}
  .sbr .inp .pre{color:var(--faint);font-weight:700;font-size:14px}
  .sbr .inp .ic{width:15px;height:15px;color:var(--faint);flex-shrink:0}
  .sbr .inp.locked{background:var(--surface);border-color:var(--line);box-shadow:none;cursor:not-allowed}
  .sbr .inp.locked input{color:var(--muted);cursor:not-allowed}
  .sbr .prefs{margin-top:24px;border-top:1px solid var(--line-soft);padding-top:22px}
  .sbr .prefs .lbl{font-size:11px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;color:#9AA4B2;margin-bottom:14px}
  .sbr .pref{display:flex;align-items:center;gap:14px;padding:13px 15px;border:1px solid var(--line);border-radius:var(--r-md);
    margin-bottom:11px;background:#fff;box-shadow:var(--sh-1)}
  .sbr .pref .pic2{width:38px;height:38px;border-radius:11px;background:var(--brand-50);border:1px solid var(--brand-100);
    color:var(--brand);display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .sbr .pref .pic2 .ic{width:18px;height:18px}
  .sbr .pref .ptx{flex:1;min-width:0}
  .sbr .pref .ptx b{font-size:13.5px;font-weight:800;color:var(--ink);display:block;letter-spacing:-.2px}
  .sbr .pref .ptx span{font-size:12px;color:var(--muted);font-weight:500}
  .sbr .langseg{display:inline-flex;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-sm);padding:3px;flex-shrink:0}
  .sbr .langseg .opt{display:flex;align-items:center;gap:7px;padding:7px 14px;border-radius:8px;font-size:12.5px;font-weight:700;
    color:var(--muted);cursor:pointer;transition:.14s;user-select:none}
  .sbr .langseg .opt.on{background:#fff;color:var(--ink);box-shadow:var(--sh-1)}
  .sbr .savebar{display:flex;align-items:center;gap:12px;margin-top:24px;padding-top:20px;border-top:1px solid var(--line)}
  .sbr .savebar .meta{font-size:12.5px;color:var(--muted);font-weight:500;display:flex;align-items:center;gap:7px}
  .sbr .savebar .meta .ic{width:14px;height:14px;color:var(--good);flex-shrink:0}
  .sbr .savebar .sp{margin-left:auto;display:flex;gap:10px}
  @media(max-width:560px){
    .sbr .pref{flex-wrap:wrap}
    .sbr .pref .ptx{flex:1 1 140px}
    .sbr .langseg{display:flex;width:100%;margin-top:4px}
    .sbr .langseg .opt{flex:1;justify-content:center}
    .sbr .savebar{flex-wrap:wrap;gap:14px}
    .sbr .savebar .meta{align-items:flex-start}
    .sbr .savebar .sp{margin-left:0;width:100%}
    .sbr .savebar .sp .btn-sbr{flex:1;justify-content:center}
  }
  /* Single column: the sub-nav scrolls with the page. Kept sticky it sat over
     the panel and the avatar slid across it on scroll. Last, so it beats the
     .setnav rule above. */
  @media(max-width:960px){.sbr .setnav{position:static}}
`;

// `platform`: rendered inside the Platform Owner Panel. The platform owner has
// no company and no company role, so the role reads "Platform owner" and the
// Company field is hidden.
const ProfilePage = ({ platform = false }) => {

    const { t, i18n } = useTranslation()
    const [profile, setProfile] = useState()
    const queryClient = useQueryClient()

    const roleText = platform
        ? t('platform.profile.role')
        : (profile?.roles?.[0]?.role ? roleLabel(profile.roles[0].role) : t('profile.owner'))

    const { data } = useQuery(['profile'], getSelf)
    const { mutate, isLoading } = useMutation(updateProfile, {
        onSuccess: onMutateSuccess((res) => {
            queryClient.invalidateQueries(['profile'])
            localStorage.setItem('cleaning-crm-token', res.token)
        })
    })

    const handleChange = (e) => setProfile({ ...profile, [e.target.name]: e.target.value })
    const handleSubmit = (e) => { e.preventDefault(); mutate(profile) }

    const handleLanguageChange = (code) => {
        i18n.changeLanguage(code)
        setProfile((p) => ({ ...p, locale: code }))
        mutate({ locale: code })
    }

    const handleAvatar = async (file) => {
        if (!profile?.id) return
        const res = await uploadUserAvatar(profile.id, file)
        if (res?.code === 200 && res.message?.avatarUrl) {
            setProfile((p) => ({ ...p, avatarUrl: res.message.avatarUrl }))
        } else {
            throw new Error(typeof res?.message === 'string' ? res.message : t('avatarUpload.uploadFailed'))
        }
    }

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setProfile(result)
            if (result?.locale && result.locale !== i18n.language) {
                i18n.changeLanguage(result.locale)
            }
        })
    }, [data, i18n])

    return (
        <div className="sbr">
            <style>{pageStyles}</style>

            {/* Desktop header */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('profile.settings')}</div>
                    <div className="sub">{t('profile.manageAccount')}</div>
                </div>
                <div className="act">
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => onQuerySuccess(data, setProfile)}>
                        <svg className="ic" style={{ width:15,height:15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 12a9 9 0 1 0 9-9 9.7 9.7 0 0 0-6.7 2.7L3 8"/><path d="M3 3v5h5"/></svg>
                        {t('common.discard')}
                    </button>
                    <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={handleSubmit} disabled={isLoading}>
                        <svg className="ic" style={{ width:15,height:15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
                        {isLoading ? t('common.saving') : t('common.save')}
                    </button>
                </div>
            </div>

            {/* Mobile header */}
            <div className="sbr-m" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.8px', color: 'var(--ink)' }}>{t('profile.settings')}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>{t('profile.accountAndPreferences')}</div>
            </div>

            <div className="setwrap">
                {/* Left sub-nav */}
                <nav className="setnav">
                    <div className="cap">{t('profile.account')}</div>
                    <button className="snav active">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        {t('profile.profile')}
                        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                </nav>

                {/* Right panel */}
                <section className="panel">
                    <div className="phd">
                        <div>
                            <div className="ti">{t('profile.profile')}</div>
                            <div className="ds">{t('profile.yourDetails')}</div>
                        </div>
                        <span className="badge-soft">{t('common.allSaved')}</span>
                    </div>

                    <div className="pbody">
                        {/* Avatar block */}
                        <div className="avrow">
                            <AvatarUpload square size={72} name={profile?.fullName} src={profile?.avatarUrl} onUpload={handleAvatar} />
                            <div className="avmeta">
                                <div className="nm">{profile?.fullName || '—'}</div>
                                <span className="rl">
                                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m12 3 1.9 4.6L18.5 9l-4.6 1.9L12 15l-1.9-4.1L5.5 9l4.6-1.4z"/></svg>
                                    {roleText}
                                </span>
                            </div>
                        </div>

                        {/* Form fields */}
                        <form onSubmit={handleSubmit}>
                            <div className="fgrid">
                                <div className="field">
                                    <label>
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                        {t('profile.fullName')}
                                    </label>
                                    <div className="inp">
                                        <input type="text" name="fullName" value={profile?.fullName ?? ''} onChange={handleChange} placeholder={t('profile.yourFullName')} />
                                    </div>
                                </div>

                                <div className="field">
                                    <label>
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.9 7.9"/></svg>
                                        {t('profile.username')}
                                    </label>
                                    <div className="inp">
                                        <span className="pre">@</span>
                                        <input type="text" name="username" value={profile?.username ?? ''} onChange={handleChange} placeholder="username" />
                                    </div>
                                </div>

                                <div className="field full">
                                    <label>
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg>
                                        {t('profile.emailAddress')}
                                    </label>
                                    <div className="inp locked">
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        <input type="email" value={profile?.email ?? ''} readOnly tabIndex={-1} />
                                    </div>
                                    <span className="hint">
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        {t('profile.emailTiedToLogin')}
                                    </span>
                                </div>

                                <div className="field">
                                    <label>
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m12 3 1.9 4.6L18.5 9l-4.6 1.9L12 15l-1.9-4.1L5.5 9l4.6-1.4z"/></svg>
                                        {t('profile.role')}
                                    </label>
                                    <div className="inp locked">
                                        <input type="text" value={roleText} readOnly tabIndex={-1} />
                                    </div>
                                    <span className="hint">
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        {platform ? t('platform.profile.roleHint') : t('profile.setByWorkspace')}
                                    </span>
                                </div>

                                {!platform && (
                                    <div className="field">
                                        <label>
                                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M19 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                                            {t('profile.company')}
                                        </label>
                                        <div className="inp locked">
                                            <input type="text" value={profile?.activeCompany?.name || '—'} readOnly tabIndex={-1} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Preferences */}
                            <div className="prefs">
                                <div className="lbl">{t('profile.preferences')}</div>
                                <div className="pref">
                                    <div className="pic2">
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                                    </div>
                                    <div className="ptx">
                                        <b>{t('profile.language')}</b>
                                        <span>{t('profile.interfaceLanguage')}</span>
                                    </div>
                                    <div className="langseg">
                                        {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                                            <div key={code} className={`opt ${i18n.language === code ? 'on' : ''}`} onClick={() => handleLanguageChange(code)}>
                                                {code.toUpperCase()} {label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Save bar */}
                            <div className="savebar">
                                <div className="meta">
                                    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
                                    {profile?.updatedAt ? t('common.lastUpdated', { date: new Date(profile.updatedAt).toLocaleDateString(i18n.language === 'sr' ? 'sr-RS' : i18n.language === 'ro' ? 'ro-RO' : 'en-GB', { day:'numeric', month:'long', year:'numeric' }) }) : t('common.notYetSaved')}
                                </div>
                                <div className="sp">
                                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => onQuerySuccess(data, setProfile)}>{t('common.cancel')}</button>
                                    <button type="submit" className="btn-sbr btn-primary-sbr" disabled={isLoading}>
                                        <svg className="ic" style={{ width:16,height:16 }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
                                        {isLoading ? t('common.saving') : t('common.save')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ProfilePage;
